/**
 * Analytics Event Sync Service
 *
 * This service handles synchronization of analytics events between Firestore and external
 * analytics platforms (Amplitude, Google Analytics, Sentry). It automatically detects
 * unsynced events and sends them to the appropriate analytics platforms.
 */

import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';

import { trackStripePurchaseSuccessful } from './analytics';
import { trackSentryError } from './sentry';
import { firestoreDb } from '../firebaseUtils';

// Types for analytics events
export interface AnalyticsEvent {
  id: string;
  event_name: string;
  user_id: string;
  timestamp: string;
  is_synced_with_analytics: boolean;
  created_at: any;
  synced_at?: any;
  [key: string]: any; // Allow additional properties based on event type
}

export interface PurchaseEvent extends AnalyticsEvent {
  checkout_session_id: string;
  amount: number;
  currency: string;
  plan_name: string;
  plan_code: string;
  subscription_id?: string;
  customer_id: string;
  purchase_number: number;
  is_upgrade: boolean;
  previous_plan?: string;
  payment_method: string;
}

/**
 * Analytics Event Sync Service Class
 * Handles automatic syncing of unsynced analytics events to external platforms
 */
export class AnalyticsEventSyncService {
  private listeners: Array<() => void> = [];
  private syncedEventIds: Set<string> = new Set();

  /**
   * Start listening for unsynced events for a specific user
   */
  startSyncingEvents(userId: string): void {
    if (!userId) {
      console.warn('[App | Analytics] Cannot start syncing - no user ID provided');
      return;
    }

    // Stop any existing listeners first
    this.stopSyncingEvents();

    console.log(`[App | Analytics] Starting event sync for user: ${userId}`);

    // Listen to purchase events
    this.listenToPurchaseEvents(userId);

    // Future: Add listeners for other event types here
    // this.listenToCampaignEvents(userId);
    // this.listenToUserEvents(userId);
  }

  /**
   * Stop all event listeners
   */
  stopSyncingEvents(): void {
    this.listeners.forEach((unsubscribe) => unsubscribe());
    this.listeners = [];
    this.syncedEventIds.clear();
    console.log('[App | Analytics] Stopped all event sync listeners');
  }

  /**
   * Listen to purchase events and sync unsynced ones
   */
  private listenToPurchaseEvents(userId: string): void {
    try {
      const purchaseEventsRef = collection(
        firestoreDb,
        'users',
        userId,
        'analytics',
        'events',
        'purchase'
      );

      // Query for unsynced events
      const unsyncedQuery = query(
        purchaseEventsRef,
        where('is_synced_with_analytics', '==', false)
      );

      const unsubscribe = onSnapshot(
        unsyncedQuery,
        (snapshot) => {
          const unsyncedEvents: PurchaseEvent[] = [];

          snapshot.forEach((doc) => {
            const eventData = doc.data();
            const event: PurchaseEvent = {
              id: doc.id,
              ...eventData,
            } as PurchaseEvent;
            unsyncedEvents.push(event);
          });

          if (unsyncedEvents.length > 0) {
            console.log(
              `[App | Analytics] Found ${unsyncedEvents.length} unsynced purchase events | Raw data:`,
              unsyncedEvents
            );

            // Process each unsynced event
            unsyncedEvents.forEach((event) => {
              this.syncPurchaseEvent(userId, event);
            });
          }
        },
        (error) => {
          trackSentryError('Failed to listen to unsynced purchase events', {
            userId,
            error,
          });
          console.error(
            '[App | Analytics] Failed to listen to purchase events:',
            error
          );
        }
      );

      this.listeners.push(unsubscribe);
      console.log('[App | Analytics] Started listening to purchase events');
    } catch (error) {
      trackSentryError('Failed to setup purchase events listener', { userId, error });
      console.error(
        '[App | Analytics] Failed to setup purchase events listener:',
        error
      );
    }
  }

  /**
   * Sync a single purchase event to external analytics platforms
   */
  private async syncPurchaseEvent(userId: string, event: PurchaseEvent): Promise<void> {
    // Prevent duplicate syncing of the same event
    if (this.syncedEventIds.has(event.id)) {
      return;
    }

    try {
      console.log(
        `[App | Analytics] Syncing purchase event: ${event.event_name} | Session: ${event.checkout_session_id} | Amount: ${event.amount}`
      );

      // Track success/failure for each analytics platform
      const syncResults: { platform: string; success: boolean; error?: any }[] = [];

      try {
        // Track the event across all analytics platforms
        // Note: trackStripePurchaseSuccessful sends to Amplitude, Google Analytics, and Sentry
        await trackStripePurchaseSuccessful({
          checkout_session_id: event.checkout_session_id,
          user_id: event.user_id,
          amount: event.amount,
          currency: event.currency,
          plan_name: event.plan_name,
          plan_code: event.plan_code,
          subscription_id: event.subscription_id || '',
          customer_id: event.customer_id,
          purchase_number: event.purchase_number,
          is_upgrade: event.is_upgrade,
          previous_plan: event.previous_plan,
          payment_method: event.payment_method,
        });

        // If we reach here, the tracking was successful
        syncResults.push({ platform: 'all_platforms', success: true });

        console.log(
          `[App | Analytics] Successfully sent event to all analytics platforms: ${event.id}`
        );
      } catch (trackingError) {
        syncResults.push({
          platform: 'all_platforms',
          success: false,
          error: trackingError,
        });
        console.error(
          `[App | Analytics] Failed to send event to analytics platforms:`,
          trackingError
        );
        throw trackingError; // Re-throw to prevent marking as synced
      }

      // Only mark as synced if all platforms received the event successfully
      const allSuccessful = syncResults.every((result) => result.success);

      if (allSuccessful) {
        // Mark as synced in Firestore
        await this.markEventAsSynced(userId, 'purchase', event.id);

        // Remember that we synced this event
        this.syncedEventIds.add(event.id);

        console.log(
          `[App | Analytics] Successfully synced purchase event: ${event.id} | Session: ${event.checkout_session_id} | All platforms confirmed`
        );
      } else {
        const failedPlatforms = syncResults
          .filter((r) => !r.success)
          .map((r) => r.platform);
        throw new Error(`Failed to sync to platforms: ${failedPlatforms.join(', ')}`);
      }
    } catch (error) {
      trackSentryError('Failed to sync purchase event', {
        userId,
        eventId: event.id,
        error,
      });
      console.error(
        `[App | Analytics] Failed to sync purchase event ${event.id}:`,
        error
      );

      // Don't re-throw here to prevent infinite retry loops
      // The event will remain unsynced and will be retried on next app load
    }
  }

  /**
   * Mark an event as synced in Firestore
   */
  private async markEventAsSynced(
    userId: string,
    eventType: string,
    eventId: string
  ): Promise<void> {
    try {
      const eventRef = doc(
        firestoreDb,
        'users',
        userId,
        'analytics',
        'events',
        eventType,
        eventId
      );

      await updateDoc(eventRef, {
        is_synced_with_analytics: true,
        synced_at: serverTimestamp(),
      });

      console.log(
        `[App | Analytics] Marked event as synced: ${eventType}/${eventId} for user ${userId}`
      );
    } catch (error) {
      trackSentryError('Failed to mark event as synced', {
        userId,
        eventType,
        eventId,
        error,
      });
      console.error(
        `[App | Analytics] Failed to mark event as synced: ${eventType}/${eventId}`,
        error
      );
      throw error;
    }
  }

  /**
   * Get sync service status for debugging
   */
  getStatus() {
    return {
      activeListeners: this.listeners.length,
      syncedEventsCount: this.syncedEventIds.size,
      syncedEventIds: Array.from(this.syncedEventIds),
    };
  }

  /**
   * Force sync all unsynced events (one-time operation)
   */
  async forceSyncAllEvents(userId: string): Promise<void> {
    if (!userId) {
      console.warn('[App | Analytics] Cannot force sync - no user ID provided');
      return;
    }

    console.log(
      `[App | Analytics] Force syncing all unsynced events for user: ${userId}`
    );

    try {
      // Force sync purchase events
      await this.forceSyncPurchaseEvents(userId);

      console.log(`[App | Analytics] Completed force sync for user: ${userId}`);
    } catch (error) {
      trackSentryError('Failed to force sync events', { userId, error });
      console.error(`[App | Analytics] Failed to force sync events:`, error);
    }
  }

  /**
   * Force sync all unsynced purchase events
   */
  private async forceSyncPurchaseEvents(userId: string): Promise<void> {
    try {
      const purchaseEventsRef = collection(
        firestoreDb,
        'users',
        userId,
        'analytics',
        'events',
        'purchase'
      );

      const unsyncedQuery = query(
        purchaseEventsRef,
        where('is_synced_with_analytics', '==', false)
      );

      const snapshot = await getDocs(unsyncedQuery);
      const unsyncedEvents: PurchaseEvent[] = [];

      snapshot.forEach((doc) => {
        const eventData = doc.data();
        const event: PurchaseEvent = {
          id: doc.id,
          ...eventData,
        } as PurchaseEvent;
        unsyncedEvents.push(event);
      });

      if (unsyncedEvents.length > 0) {
        console.log(
          `[App | Analytics] Force syncing ${unsyncedEvents.length} purchase events | Raw data:`,
          unsyncedEvents
        );

        // Process each event sequentially to avoid overwhelming the system
        for (const event of unsyncedEvents) {
          await this.syncPurchaseEvent(userId, event);
          // Small delay between events to prevent rate limiting
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      } else {
        console.log(
          '[App | Analytics] No unsynced purchase events found for force sync'
        );
      }
    } catch (error) {
      trackSentryError('Failed to force sync purchase events', { userId, error });
      console.error('[App | Analytics] Failed to force sync purchase events:', error);
      throw error;
    }
  }
}

// Singleton instance
export const analyticsEventSyncService = new AnalyticsEventSyncService();

/**
 * Utility function to start syncing events for a user
 */
export function startEventSync(userId: string): void {
  analyticsEventSyncService.startSyncingEvents(userId);
}

/**
 * Utility function to stop syncing events
 */
export function stopEventSync(): void {
  analyticsEventSyncService.stopSyncingEvents();
}

/**
 * Utility function to force sync all unsynced events
 */
export function forceSyncAllEvents(userId: string): Promise<void> {
  return analyticsEventSyncService.forceSyncAllEvents(userId);
}

/**
 * Debug function to check event sync health
 */
export function getEventSyncHealth() {
  return {
    syncService: analyticsEventSyncService.getStatus(),
    timestamp: new Date().toISOString(),
  };
}

// Export for debugging in development
if (process.env.NODE_ENV === 'development') {
  (window as any).analyticsEventSync = {
    service: analyticsEventSyncService,
    start: startEventSync,
    stop: stopEventSync,
    forceSync: forceSyncAllEvents,
    health: getEventSyncHealth,
  };
}
